import ScholarshipInfo from "./ScholarshipInfo";
import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from 'react-i18next';
import { LoginContext } from "./LoginContext.js";
import ScholarshipService from "../services/ScholarshipService";

/**
 *
 */
export default function ScholarshipList(props) {
    const user = useContext(LoginContext);
    const { t, i18n } = useTranslation();
    const [onlyOpen, setOnlyOpen] = useState(0);
    const [myList, setMyList] = useState(JSON.parse(sessionStorage.getItem("myList")) || []);
    const [idsInDB, setIdsInDB] = useState([]);
    const isLoggedIn = user && user.authenticated;
    const [removeSuccess, setRemoveSuccess] = useState(null)
    const [showInfoPendingApplication, setShowInfoPendingApplication] = useState(false);
    const [showErroMessageApplyButton, setShowErroMessageApplyButton] = useState(false);
    const [activeApplications, setActiveApplications] = useState([]);

    useEffect(() => { 
        if (isLoggedIn) { 
            ScholarshipService.getActiveApplicationsForUser()
            .then(response => {
                setActiveApplications(response.data)
            })
            .catch(e => {
                console.log(e);
            });
        }
    }, [isLoggedIn]);

    // Whenever myList change save to sessionStorage
    useEffect(() => {
        sessionStorage.setItem('myList', JSON.stringify(myList));
    }, [myList]);

    useEffect(()=>{
        // if user has logged in, retrieve scholarships already saved and show in myList
        if (user && user.authenticated) {
            ScholarshipService.getScholarshipsInApplication()
                .then(response => {
                    if (props.allScholarships && props.allScholarships.length > 0) {
                        const idsInDB = response.data;  // ids in application saved in db
                        setIdsInDB(idsInDB);

                        // We use functional update here because we can't explicitly use myList because
                        // then it would have to be a dependency to this useEffect and would lead to
                        // an infinite loop. Functional update: React let you pass a function to setState instead of a value.
                        // That function will receive the current state as the first parameter and the returned value will become the new state.
                        setMyList(currentMyList => {
                            const idsInMyList = currentMyList.map(s => s.id);
                            const unionIds = idsInDB.concat(idsInMyList);

                            const idsWithoutDuplicates = unionIds.filter((value, pos) => {
                                return unionIds.indexOf(value) === pos;
                            });
                            const newMyList = [];
                            idsWithoutDuplicates.forEach(id => {
                                const s = props.allScholarships.find(s => id === s.id);
                                if (s) {
                                    newMyList.push(s);
                                }
                            })
                        
                            //visa varning när nya stipendier läggs till i en pågående ansökan.
                            if(idsInMyList.length > 0 && (newMyList.length > idsInMyList.length)) {
                                setShowInfoPendingApplication(true)
                            }

                            return newMyList;
                        });
                    }
                })
                .catch(e => {
                    console.log(e);
                });
        }
    }, [props.allScholarships, user]);

    const onlyOpenCheckboxChanged = () => {
        setOnlyOpen(onlyOpen === 0 ? 1 : 0);
    }

    const filterOnlyOpen = (scholarships) => {
        return onlyOpen ? scholarships.filter(scholarship => scholarship.utlyst === true) : scholarships
    }

    const nrOfFilteredScholarships = () => {
        return filterOnlyOpen(props.scholarships).length === props.totalNrOfScholarships ? props.totalNrOfScholarships : filterOnlyOpen(props.scholarships).length + t('scholarship_list.of') + props.totalNrOfScholarships
    }

    /**
     * Lägger till stipendiet i "MyList" och visar en informationtext om det redan finns en påbörjad ansökan för sökomgången.
     */
    const addScholarshipToMyList = (scholarship) => {
        hideShowAlert()
        setMyList([...myList, scholarship])

        if(checkIfPendingApplication(scholarship)) {
            setShowInfoPendingApplication(true)
        }
    }

    /**
     * Kontrollera om det redan finns en påbörjad ansökan för sökomgången.
     */
    function checkIfPendingApplication(scholarship) {
        let scholarshipsInApplication = idsInDB.map(scholarshipId => props.allScholarships.find(s => scholarshipId === s.id))
        return scholarshipsInApplication.some(s => s.kungorelseomgang === scholarship.kungorelseomgang)
    }

    /**
     * Varning som visas om man lägger till nya stipendier i en redan pågående ansökan.
     */
     function alertPendingApplication() {
        if(isLoggedIn && showInfoPendingApplication) {
            return (
                <div className="alert alert-warning alert-close d-flex align-items-start mt-3" role="alert" aria-atomic="true" aria-label="exampleModalLabel" aria-hidden="false">
                    <div>
                        <h3 className="icon-position-left icon-warning">{t('scholarship.note')}</h3>
                        <p>{t('scholarship.info_message_pending_application')}</p>
                    </div>
                    <button type="button" className="is-remove-space-small-negative button-text button-icon button-compact icon-clear active" data-bs-dismiss="alert" aria-label="Close"></button>
                </div>
            )
        }
    }

    /**
     * Kontrollerar om stipendiet finns i en påbörjad ansökan och i så fall ansopas en rest-tjänst som tar bort stipendiet från ansökan. Ett bekräftelsemeddelande visas om borttaget gick bra annars visas ett felmeddelande.
     * 
     * Om det inte finns en påbörjad ansökan så tas stipendiet bara bort från "MyList".
     */
    const removeScholarshipFromMyList = (scholarship) => {
        hideShowAlert()
        
        if(checkIfScholarshipInApplication(scholarship.id)) {
            ScholarshipService.deleteScholarshipInApplication(scholarship.kungorelseomgang, scholarship.termin, scholarship.id)
            .then(() => {
                setMyList(myList => {
                    return myList.filter(scholarschip => scholarschip.id !== scholarship.id)
                })
                //Visa ett bekräftelsemeddelande
                setRemoveSuccess({success:true, scholarship_name: scholarship.namn})
            }).catch(e => {
                //Visa ett felmeddelande
                console.log("Fel vid borttagning av " + scholarship.namn, e);
                setRemoveSuccess({success:false, scholarship_name: scholarship.namn})
            });
        }
        else {
            setMyList(myList => {
                return myList.filter(scholarschip => scholarschip.id !== scholarship.id)
            })
        }
    }

    /**
     * Scrollar till det valda stipendiet i listan och highlightar det tillfälligt.
     */
    const scrollToScholarship = (scholarshipId) => {
        const element = document.getElementById(`scholarship-${scholarshipId}`);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            
            element.classList.add('border', 'border-warning', 'border-3');
            setTimeout(() => {
                element.classList.remove('border', 'border-warning', 'border-3');
            }, 2000);
        }
    }

    /**
     * Kontrollerar om stipendiet finns i en pågående ansökan.
     */
    function checkIfScholarshipInApplication(scholarshipId) {
        return idsInDB.some(id => id === scholarshipId)
    }

    function alertRemoveScholarship() {
        if(removeSuccess !== null && removeSuccess.success === true) {
            return (<div className="alert alert-success mt-3" role="alert">
                <p>{t('scholarship.remove_success_message', {scholarship: removeSuccess.scholarship_name})}</p>
            </div>)
        }
        else if(removeSuccess !== null && removeSuccess.success === false) {
            return (<div className="alert alert-danger" role="alert">
                <h3 className="icon-position-left icon-warning">{t('scholarship.remove_error_message_title')}</h3>
                <p>{t('scholarship.remove_error_message', {scholarship: removeSuccess.scholarship_name})}</p>
            </div>)
        }
    }

    function checkIfInMyList(id) {
        return myList.some(scholarship => scholarship.id === id)
    }

    /**
     * Varning som visas om man har flera aktiva ansökningar från olika sökomgångar.
     */
    function alertSeveralKungOmg() {
        if(isLoggedIn && Object.keys(myListGroupByKungorelseomgang).length > 1) {
            return (
                <div className="alert alert-warning alert-close d-flex align-items-start mt-3" role="alert" aria-atomic="true" aria-label="exampleModalLabel" aria-hidden="false">
                    <div>
                        <h3 className="icon-position-left icon-warning">{t('scholarship.note')}</h3>
                        <p>{t('scholarship.info_message_several_kungOmg')}</p>
                    </div>
                    <button type="button" className="is-remove-space-small-negative button-text button-icon button-compact icon-clear active" data-bs-dismiss="alert" aria-label="Close"></button>
                </div>
            )
        }
    }

    function errorMessageAfterApply() {
        if (showErroMessageApplyButton) {
            return (
                <div className={'alert alert-danger'} role={'alert'}>
                    <p className={'icon-position-left icon-warning'}>{t('scholarship_list.apply_button_error_message')}</p>
                </div>
            )
        }
    }

    function hideShowAlert() {
        setShowInfoPendingApplication(false)
        setRemoveSuccess(null)
        setShowErroMessageApplyButton(false)
    }
   
    const getApplication = (kungorelseId) => {
        if (isLoggedIn && activeApplications !== null && activeApplications.length > 0) {
            const application = activeApplications.find(a => a.kungorelse === kungorelseId)
            return application;
        }
    }

    /**
     * Om det redan finns en påbörjad/inskickad ansökan, skriv ut status.
     * @param  kungorelseId 
     */
    const renderApplicationStatus = (kungorelseId) => {
        if (isLoggedIn) {
            const application = getApplication(kungorelseId);

            if(application && (application.status === 'NY' || application.status === 'SOKT' || application.status === 'SOKT_ANDRAS')) {
                return (
                    <div className="card-body">
                        <p className="card-text mb-0"><span>{t('scholarship_list.application_status_' + application.status)}</span></p>

                        { application.status === 'NY' &&
                            <p className="alert alert-warning mb-0 mt-3">
                                {t('scholarship_list.application_warning_' + application.status)}
                            </p>
                        }
                        { application.status === 'SOKT_ANDRAS' &&
                            <p className="alert alert-warning mb-0 mt-3">
                                {t('scholarship_list.application_warning_' + application.status)}
                            </p>
                        }
                    </div>
                )
            }
        }
    }

    const applyButtonText = (kungorelseId) => {
        const application = getApplication(kungorelseId)
        return application ? t('scholarship_list.apply_button_' + application.status) : t('scholarship_list.apply_button')
    }

    /**
     * Ritar ut alla stipendier som tillhör en viss kungörelseomgång.
     *
     */
    function renderKungorelseomgang(myListKungorelseomgang, key) {
        return (
            <>
            { alertRemoveScholarship() }

            <div className="card mt-3" key={"kungorelseomgang-" + key}>
                <h4 className="card-header"><small>{t('scholarship.open_till')} {myListKungorelseomgang[0].ansokningsdatum} {myListKungorelseomgang[0].ansokningstid}</small></h4>
                { renderApplicationStatus(key) }
                
                <ul className="list-group list-group-flush ">
                    {myListKungorelseomgang.sort((a, b) => a.namn > b.namn ? 1 : -1).map((scholarship) => (
                        <li key={scholarship.id} className="list-group-item card-body link-block">
                            <div className="row">
                                <div className="col-7">
                                    <h3 
                                        className="text-decoration-underline"
                                        onClick={() => scrollToScholarship(scholarship.id)} 
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => e.key === 'Enter' && scrollToScholarship(scholarship.id)}
                                        style={{cursor: 'pointer'}}
                                        title={t('scholarship_list.go_to_scholarship')}
                                        aria-label={`${t('scholarship_list.go_to_scholarship')} - ${scholarship.namn}`}
                                    >
                                        {scholarship.namn}
                                    </h3>
                                    <p className="card-text mb-0">
                                        {(scholarship.kategori !== t('scholarship.categories.nation') && scholarship.kategori !== t('scholarship.categories.faculty')) &&
                                            <span>{scholarship.kategori}</span>}
                                    </p>
                                    <p className="card-text mb-0">
                                        {(scholarship.nationer.length > 0 || scholarship.fakulteter.length > 0) &&
                                            <span>{scholarship.nationer.concat(scholarship.fakulteter).join(', ')}</span>
                                        }
                                    </p>
                                </div>
                                <div className="col-5">
                                    {myListKungorelseomgang[0].ansokningsdatum !== null &&
                                        <button className="button button-text button-small button-icon icon-remove float-end" onClick={() => removeScholarshipFromMyList(scholarship)}>{t('scholarship_list.remove_from_my_list')}
                                        </button>
                                    }
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
                {isLoggedIn && myListKungorelseomgang[0].ansokningsdatum !== null &&
                    <div className="card-body">
                        <button className="button button-outline" onClick={() => {
                            ScholarshipService.saveApplication(myListKungorelseomgang.map(s => s.id), myListKungorelseomgang[0].kungorelseomgang, myListKungorelseomgang[0].termin)
                                .then(response => {
                                    window.location.href = '/stipendium/stipac/ansokan/ansokning/personuppgifter';
                                })
                                .catch(e => {
                                    console.log(e);
                                    setShowErroMessageApplyButton(true);
                                });
                        } }>{ applyButtonText(key) }</button>
                    </div>}
            </div>
            </>
        );
    }

    /** 
     * Listan "myList" kan innehålla stipendier från flera kungörelseomgångar, gruppera listan efter kungörelseomgång. 
     */
    function renderMyList() {
        return (
            myList && myList.length > 0
                ? ( 
                    Object.keys(myListGroupByKungorelseomgang).map(key => {
                        return renderKungorelseomgang(myListGroupByKungorelseomgang[key], key)
                    })
                )
                : (
                    <div className="card mt-3">
                        <ul className="list-group list-group-flush ">
                            <li className="list-group-item card-body link-block">{t('scholarship_list.empty_my_list')}</li>
                        </ul>
                    </div>
                )
        );
    }

    const myListGroupByKungorelseomgang = groupBy(myList, "kungorelseomgang");

    function groupBy(objectArray, property) {
        return objectArray.reduce((acc, obj) => {
            const key = obj[property];
            if (!acc[key]) {
                acc[key] = [];
            }
            // Add object to list for given key's value
            acc[key].push(obj);
            return acc;
        }, {});
    }

    return (
        <section aria-labelledby="searchFilterResults ">
            <h3 className="visually-hidden" id="searchFilterResults">{t('scholarship_list.search_result')}</h3>
          
            <div className="row mt-4 d-flex flex-row-reverse">
                <div className="col-lg-4">
                    <h3>{t('scholarship_list.my_list_title')}</h3>
                    { alertPendingApplication() }
                    { alertSeveralKungOmg() }
                    { errorMessageAfterApply() }
                    { renderMyList() }
                    { !isLoggedIn  &&
                        <a href={'/stipendium/stipac/login?lang=' + i18n.resolvedLanguage} className="button button-icon-right icon-call-to-action float-end mb-5">{t('scholarship_list.log_in_to_apply')}</a>
                    }
                </div>
                <div className="col-lg-8">
                    <div className="card mt-5">
                        <h2 className="card-header">{t('scholarship_list.title_filtered_scholarships')} ({nrOfFilteredScholarships()})</h2>
                        <div className="card-body">                            
                            <div className="form-check">
                                <input className="form-check-input" type="checkbox" onChange={() => onlyOpenCheckboxChanged()} id="sokbara" />
                                <label className="form-check-label" htmlFor="sokbara">
                                    {t('scholarship_list.checkbox_scholarships_open_for_application')}
                                </label>
                            </div>
                        </div>
                        <ul className="list-group list-group-flush detailStip">
                            {props.scholarships && filterOnlyOpen(props.scholarships).map((scholarship) =>
                                <ScholarshipInfo key={scholarship.id} scholarship={scholarship} isInMyList={checkIfInMyList(scholarship.id)} onSaveSholarshipClick={() => addScholarshipToMyList(scholarship)} onRemoveScholarshipClick={() => removeScholarshipFromMyList(scholarship)} />
                            )}
                            {(!props.scholarships || filterOnlyOpen(props.scholarships).length === 0) &&
                                <span className="p-4">{t('scholarship_list.empty_result')}</span>
                            }
                        </ul>
                    </div>
                </div>
            </div>
        </section>
    );
}